import { useState, useRef, useEffect, useCallback } from 'react';
import type { TouchEvent } from 'react';
import { useViewport } from '../../hooks/useViewport';

interface SwipeableCarouselProps {
  items: React.ReactNode[];
  showDots?: boolean;
  showArrows?: boolean;
  autoPlay?: boolean;
  interval?: number;
  className?: string;
  onSlideChange?: (index: number) => void;
}

/**
 * Touch-enabled carousel component with swipe gestures.
 * Supports auto-play, dot indicators, and navigation arrows.
 * Respects reduced motion preferences.
 */
export function SwipeableCarousel({
  items,
  showDots = true,
  showArrows = true,
  autoPlay = true,
  interval = 5000,
  className = '',
  onSlideChange,
}: SwipeableCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoPlay);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile, prefersReducedMotion } = useViewport();

  // Minimum swipe distance to trigger slide change
  const minSwipeDistance = 50;

  const goToSlide = useCallback((index: number) => {
    const newIndex = Math.max(0, Math.min(index, items.length - 1));
    setCurrentIndex(newIndex);
    onSlideChange?.(newIndex);
  }, [items.length, onSlideChange]);

  const goToNext = useCallback(() => {
    goToSlide(currentIndex === items.length - 1 ? 0 : currentIndex + 1);
  }, [currentIndex, items.length, goToSlide]);

  const goToPrev = useCallback(() => {
    goToSlide(currentIndex === 0 ? items.length - 1 : currentIndex - 1);
  }, [currentIndex, items.length, goToSlide]);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || items.length <= 1 || prefersReducedMotion) return;

    const timer = setInterval(goToNext, interval);
    return () => clearInterval(timer);
  }, [isAutoPlaying, items.length, interval, goToNext, prefersReducedMotion]);

  // Pause auto-play on user interaction
  const pauseAutoPlay = useCallback(() => {
    setIsAutoPlaying(false);
    // Resume after 10 seconds of no interaction
    const timer = setTimeout(() => setIsAutoPlaying(autoPlay), 10000);
    return () => clearTimeout(timer);
  }, [autoPlay]);

  // Touch handlers for swipe gestures
  const handleTouchStart = (e: TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!touchStart) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
      pauseAutoPlay();
    } else if (isRightSwipe) {
      goToPrev();
      pauseAutoPlay();
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  const handleDotClick = (index: number) => {
    goToSlide(index);
    pauseAutoPlay();
  };

  const handleArrowClick = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      goToPrev();
    } else {
      goToNext();
    }
    pauseAutoPlay();
  };

  if (items.length === 0) return null;

  const transitionClasses = prefersReducedMotion ? '' : 'transition-all duration-500';

  return (
    <div className={`relative ${className}`}>
      {/* Carousel Container */}
      <div
        ref={containerRef}
        className="overflow-hidden touch-pan-y"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className={`flex ${transitionClasses} ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
          }}
        >
          {items.map((item, index) => (
            <div
              key={index}
              className="w-full flex-shrink-0"
              aria-hidden={index !== currentIndex}
            >
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      {showArrows && items.length > 1 && !isMobile && (
        <>
          <button
            onClick={() => handleArrowClick('prev')}
            className={`touch-target absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 p-3 rounded-full bg-white shadow-lg hover:shadow-xl ${transitionClasses} hover:bg-gray-50 group z-10`}
            aria-label="Previous slide"
          >
            <svg className="w-5 h-5 text-gray-600 group-hover:text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={() => handleArrowClick('next')}
            className={`touch-target absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 p-3 rounded-full bg-white shadow-lg hover:shadow-xl ${transitionClasses} hover:bg-gray-50 group z-10`}
            aria-label="Next slide"
          >
            <svg className="w-5 h-5 text-gray-600 group-hover:text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Dot Indicators */}
      {showDots && items.length > 1 && (
        <div className="flex items-center justify-center mt-6 sm:mt-8 gap-2" role="tablist">
          {items.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`touch-target-sm h-2.5 sm:h-3 rounded-full ${transitionClasses} ${
                index === currentIndex 
                  ? 'bg-primary-600 w-6 sm:w-8' 
                  : 'bg-gray-300 hover:bg-gray-400 w-2.5 sm:w-3'
              }`}
              role="tab"
              aria-selected={index === currentIndex}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default SwipeableCarousel;
